import React, { useState, useEffect, useCallback } from 'react';
import { Search, AlertCircle, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { productsApi } from '../../../../services/api';
import { Product } from '../../../../types';

interface ProductSelectionProps {
  selectedProducts: Product[];
  onProductsSelected: (products: Product[]) => void;
}

const ProductSelection: React.FC<ProductSelectionProps> = ({ selectedProducts, onProductsSelected }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<number[]>(selectedProducts.map(p => p.id));
  const [selectedProductsData, setSelectedProductsData] = useState<Product[]>(selectedProducts);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productsApi.getAll({
        active: true,
        search: debouncedSearch,
        page: currentPage,
        limit: itemsPerPage
      });

      // Filtrar apenas produtos com estoque ou sem premio
      const availableProducts = response.products.filter(p =>
        p.is_active && (p.stock > 0 || p.is_no_prize)
      );

      setProducts(availableProducts);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalCount(response.pagination?.total || availableProducts.length);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);


  const toggleProduct = (product: Product) => {
    if (selected.includes(product.id)) {
      // Remove from selection
      setSelected(selected.filter(id => id !== product.id));
      setSelectedProductsData(selectedProductsData.filter(p => p.id !== product.id));
    } else {
      // Add to selection
      setSelected([...selected, product.id]);
      setSelectedProductsData([...selectedProductsData, product]);
    }
  };

  const handleContinue = () => {
    onProductsSelected(selectedProductsData);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bordo"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-tinta leading-tight">
          Selecione os <span className="italic text-bordo">produtos</span>
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-tinta-soft italic">
          Escolha os produtos que o ganhador receberá
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tinta-soft w-4 h-4 sm:w-5 sm:h-5" />
        <input
          type="text"
          placeholder="Buscar produtos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-tinta/15 rounded-lg text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
        />
        {loading && debouncedSearch !== search && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-bordo"></div>
          </div>
        )}
      </div>

      {/* Selected Count */}
      {selected.length > 0 && (
        <div className="bg-creme-light border border-tinta/10 rounded-lg p-3 flex items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-tinta">
            Selecionados
          </span>
          <span className="bg-mostarda/20 text-mostarda-dark border border-mostarda/40 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">
            {selected.length} produto{selected.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bordo"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-10 italic text-tinta-soft">
          <AlertCircle className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-tinta-soft/50 mb-2 sm:mb-3 not-italic" />
          <p className="text-sm sm:text-base">Nenhum produto disponível</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map((product) => {
            const isSelected = selected.includes(product.id);
            return (
              <div
                key={product.id}
                onClick={() => toggleProduct(product)}
                className={`relative rounded-lg p-3 cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-bordo/8 border-2 border-bordo'
                    : 'bg-white border border-tinta/15 hover:border-tinta/30'
                }`}
              >
                {/* Checkbox */}
                <div className="absolute top-2 right-2">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-bordo border-bordo'
                      : 'border-tinta/30 bg-white'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-creme" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Product Image */}
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-24 sm:h-32 object-cover rounded-md mb-2 border border-tinta/10"
                  />
                )}

                {/* Product Info */}
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-tinta mb-1 pr-6 leading-tight">{product.name}</h3>
                  <div className="flex items-center gap-2 text-xs">
                    {product.is_no_prize ? (
                      <span className="bg-mostarda/20 text-mostarda-dark border border-mostarda/40 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">
                        Sem Prêmio
                      </span>
                    ) : (
                      <span className="text-tinta-soft">Estoque: <span className="font-bold text-tinta">{product.stock}</span></span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-tinta/10">
          <p className="text-xs sm:text-sm text-tinta-soft italic">
            Página <span className="font-bold text-tinta not-italic">{currentPage}</span> de <span className="font-bold text-tinta not-italic">{totalPages}</span> ({totalCount} produtos)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 text-tinta-soft border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 text-tinta-soft border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-tinta/10">
        <button
          onClick={handleContinue}
          disabled={selected.length === 0}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuar ({selected.length})
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ProductSelection;
